import { Platform } from 'react-native';

import {
  getCurrentEnvironment,
  getEnvironmentConfig,
} from '@/constants/config';
import { storage, storageKeys } from '@/constants/storage';
import { getClientTrackingProperties } from '@/utils/clientIdentity';
import { Logger } from '@/utils/logging';
import { DevTracker } from '@/utils/tracking/trackers/devTracker';

import type {
  TrackerDestination,
  TrackingContext,
  TrackingDestinationPreset,
  TrackingInterface,
  TrackingOptions,
  TrackingUserInfoContext,
} from './_types';
import { shouldRegisterRealTrackers } from './shouldRegisterRealTrackers';
import { shouldSetAnonymousIdentity } from './shouldSetAnonymousIdentity';
// @ts-expect-error - Tracker is defined in two files for each platform
import { clarityTracker } from './trackers/clarityTracker';
// @ts-expect-error - Tracker is defined in two files for each platform
import { DatadogTracker } from './trackers/datadogTracker';
// @ts-expect-error - Tracker is defined in two files for each platform
import { googleAnalyticsTracker } from './trackers/googleAnalyticsTracker';
import { launchDarklyTracker } from './trackers/launchDarklyTracker';
import { metaPixelTracker } from './trackers/metaPixelTracker';
// @ts-expect-error - Tracker is defined in two files for each platform
import { rudderstackTracker } from './trackers/rudderstackTracker';
import { sentryTracker } from './trackers/sentryTracker';
import { DESTINATION_PRESETS, TRACKER_IDS } from './trackingConstants';
import { TrackingHandler } from './trackingHandler';

const loggerHandler = TrackingHandler();

let resolveTrackerReady: () => void;
const trackerReady = new Promise<void>((resolve) => {
  resolveTrackerReady = resolve;
});

async function initializeTrackers() {
  const currentEnv = getCurrentEnvironment();

  if (__DEV__) {
    loggerHandler.addTracker(TRACKER_IDS.dev, DevTracker());
  }

  // Real trackers register in any real build, and in dev builds whenever the
  // env is non-production — so tracking can be developed and verified locally
  // (against the QA/staging sandbox) without polluting production analytics.
  if (shouldRegisterRealTrackers(__DEV__, currentEnv)) {
    const config = getEnvironmentConfig();

    // Fail-soft per tracker: each registration is attempted independently so
    // one tracker failing to construct does not silence the rest (or block
    // app startup).
    const tryAddTracker = (id: string, factory: () => TrackingInterface) => {
      try {
        loggerHandler.addTracker(id, factory());
      } catch (error) {
        Logger.error(`[TRACKING] Failed to register tracker: ${id}`, {
          error,
        });
      }
    };

    tryAddTracker(TRACKER_IDS.clarity, () =>
      clarityTracker(
        Platform.select({
          web: config.clarityWebProjectId,
          default: config.clarityProjectId,
        }),
        currentEnv,
      ),
    );
    tryAddTracker(TRACKER_IDS.sentry, () =>
      sentryTracker(config.sentryDsn, currentEnv),
    );
    tryAddTracker(TRACKER_IDS.rudderstack, () =>
      rudderstackTracker(
        config.rudderstackWriteKey,
        config.rudderstackDataPlaneUrl,
      ),
    );
    tryAddTracker(TRACKER_IDS.datadog, () =>
      DatadogTracker(
        config.datadogClientToken,
        config.datadogAppId,
        currentEnv,
      ),
    );
    tryAddTracker(TRACKER_IDS.googleAnalytics, () => googleAnalyticsTracker());
    tryAddTracker(TRACKER_IDS.metaPixel, () => metaPixelTracker());
    tryAddTracker(TRACKER_IDS.launchDarkly, () => launchDarklyTracker());
  }

  await loggerHandler.initialize();

  // Same four values the API headers and RudderStack carry, applied as
  // session-wide tags so Clarity, Datadog RUM and Sentry can segment by them.
  loggerHandler.setTags(getClientTrackingProperties());

  // After initializing the trackers, we need to get the anonymous user ID from the
  // rudderstack tracker and set it the storage and the logger handler to sync all the trackers
  if (loggerHandler.hasTracker(TRACKER_IDS.rudderstack)) {
    const tracker = loggerHandler.getTracker(
      TRACKER_IDS.rudderstack,
    ) as TrackingInterface & {
      getAnonymousUserId: () => Promise<string | undefined>;
    };

    // Prefer our own persisted mirror over asking the SDK directly: on a
    // returning visit the SDK's own getAnonymousId() can return undefined
    // while its plugin bundles are still loading, which used to make us
    // mint a throwaway id and stomp the real persisted one (SQR-14053).
    const anonymousUserId =
      storage.getString(storageKeys.rudderstackAnonymusUserId) ??
      (await tracker.getAnonymousUserId());

    if (anonymousUserId) {
      storage.set(storageKeys.rudderstackAnonymusUserId, anonymousUserId);

      // Only assert the anonymous identity when no customer is logged in.
      // For logged-in sessions, useIdentifyCustomer owns identity — and on
      // native this anon reset can resolve after that hook has already
      // identified the customer, clobbering it back to anonymous (SQR-12581).
      const customerId = storage.getString(storageKeys.customerId);

      if (shouldSetAnonymousIdentity(customerId)) {
        loggerHandler.setUserId(`anon_${anonymousUserId}`, {
          type: 'anonymous',
          anonymousId: anonymousUserId,
        });
      }
    }
  }

  if (loggerHandler.hasTracker(TRACKER_IDS.clarity)) {
    const tracker = loggerHandler.getTracker(
      TRACKER_IDS.clarity,
    ) as TrackingInterface & {
      getTrackingSessionUrl: () => Promise<string | undefined>;
    };

    const trackingSessionUrl = await tracker.getTrackingSessionUrl();
    if (trackingSessionUrl) {
      storage.set(storageKeys.trackingSessionUrl, trackingSessionUrl);
    }
  }
}

export const Tracker = {
  options: {},
  ready: trackerReady,
  async initialize() {
    try {
      await initializeTrackers();
    } finally {
      resolveTrackerReady();
    }
  },
  whitelist(preset: TrackingDestinationPreset): TrackingOptions {
    return { whitelist: preset };
  },
  track(message: string, context?: TrackingContext, options?: TrackingOptions) {
    const enrichedContext = {
      ...context,
      anonymousUserId: storage.getString(storageKeys.rudderstackAnonymusUserId),
    };

    const whitelist = options?.whitelist ?? 'analytics';
    const destinations: readonly TrackerDestination[] =
      typeof whitelist === 'string'
        ? DESTINATION_PRESETS[whitelist]
        : whitelist;

    if (__DEV__) {
      // Dev deliberately ignores the whitelist and fans out to every registered
      // tracker, so tracking can be verified locally against the sandbox.
      // LaunchDarkly is carved out of that fan-out: an experiment destination
      // must only ever receive events that explicitly address it, otherwise
      // every unrelated app event lands in the experiment data.
      loggerHandler.trackWithWhitelist(message, enrichedContext, options, {
        _excludedTrackers: [TRACKER_IDS.launchDarkly],
      });

      // LD is the one destination that still reaches its real client in dev —
      // firing against the LD sandbox is the only QA loop for an experiment.
      if (destinations.includes('launchDarkly')) {
        loggerHandler.trackWithWhitelist(message, enrichedContext, options, {
          _resolvedWhitelist: [TRACKER_IDS.launchDarkly],
        });
      }

      return;
    }

    const resolvedWhitelist = destinations.map((dest) => TRACKER_IDS[dest]);

    loggerHandler.trackWithWhitelist(message, enrichedContext, options, {
      _resolvedWhitelist: resolvedWhitelist,
    });
  },
  async flush(destinations: readonly TrackerDestination[]) {
    const resolvedIds = destinations.map((dest) => TRACKER_IDS[dest]);
    await loggerHandler.flushTrackers(resolvedIds);
  },
  trackScreenName(name: string, params: Record<string, string>) {
    loggerHandler.trackScreenName?.(name, params);
  },
  setUserId(userId: string, context?: TrackingUserInfoContext) {
    loggerHandler.setUserId?.(userId, context);
  },
  setTags(tags: Record<string, string>) {
    loggerHandler.setTags?.(tags);
  },
};
